import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  notification,
  Row,
  Select,
  Spin,
} from 'antd'
import './InspectionReportDrawer.style.less'
import {
  DeleteOutlined,
  DoubleRightOutlined,
  EditOutlined,
} from '@ant-design/icons'
import {
  startCase,
  camelCase,
  size,
  filter,
  includes,
  get,
  find,
  entries,
  isEqual,
  values,
} from 'lodash'
import { INSPECTION_SECTION_RESULT } from '@/constant/status'
import { useEffect, useState } from 'react'
import { queryClient } from '@/config/react-query'
import { DATE_FORMAT } from '@/constant/date'
import {
  ControlType,
  REPORT_CONTROL_TYPES,
} from '@/screens/QSF/InspectionReport/InSpectionReport.constant'
import EditableChoiceListComponent from './EditableChoiceListComponent'
import {
  getTaskGroupEntry,
  useDeleteGroupPhoto,
  useUpdateCheckpoint,
} from '@/screens/Tasks/TasksLandingPage/TaskTable/TaskTable.hook'
import moment from 'moment'
import GroupCheckpointComponent from './GroupCheckpointComponent'
import ModalConfirm from '../ModalConfirm/ModalConfirm'
import {
  getInvalidDependentsToClear,
  resolveDependentSelectState,
  sanitizeSelectValueForDependency,
} from './checkpointDependencySelect'
import { getTriggerAnswerValue } from '@/utils/checkpointDependency'

const {
  MULTI_SELECT,
  SELECT,
  TEXT,
  NUMBER,
  DATE,
  EDITABLE_ENTRY_LIST,
  SELECT_DROPDOWN,
  GROUP,
  PHOTO,
} = REPORT_CONTROL_TYPES

const { TextArea } = Input
const { DDMMYY } = DATE_FORMAT

type DATA_TYPE = {
  id: string
  name: string
  value: []
  title?: string
  display_order?: number
  control_type?: string
  entryIndex?: number
  addNewPhotoKey?: string
}
interface Props {
  onClose: (v) => void
  open: boolean
  dataSource: any
  data: DATA_TYPE
  index: number
  dependencyTemplateSections?: any[]
  reportSections?: any[]
}

// TODO: Every using this component need to provide the type of data
function InspectionReportDrawer(props: Props) {
  const { RESULT_ARRAY, RESULT_OBJECT } = INSPECTION_SECTION_RESULT
  const controlType = get(props.data, 'control_type', '')
  const [stateIsDeletePhoto, setStateIsDeletePhoto] = useState(null)
  const [stateConfirmDelete, setStateConfirmDelete] = useState(false)
  const [stateEnableSubmitButton, setStateEnableSubmitButton] = useState(true)

  const [form] = Form.useForm()

  const updateCheckpointMutation = useUpdateCheckpoint()
  const clearDependentCheckpointMutation = useUpdateCheckpoint()
  const deleteCheckpointGroupPhoto = useDeleteGroupPhoto()
  const { data: groupCheckpointData, isLoading: taskGroupEntryLoading } =
    getTaskGroupEntry(props.data?.id, props.data?.entryIndex)

  const { error, isSuccess, isError }: any = updateCheckpointMutation

  const [mouseOver, setMouseOver] = useState(false)

  const handleDeletePhoto = (dataDelete: any) => {
    deleteCheckpointGroupPhoto.mutate(dataDelete)
    setStateConfirmDelete(false)
    handleCloseForm()
  }

  const handleCloseForm = () => {
    setStateEnableSubmitButton(true)
    setStateIsDeletePhoto(null)
    props.onClose(false)
  }

  useEffect(() => {
    const isOpenSelect = document.querySelector('.ant-select-open')
    const allowScrolling = !mouseOver && !isOpenSelect
    if (allowScrolling) {
      document.body.style.overflow = 'auto'
    } else {
      document.body.style.overflow = ''
    }
  }, [mouseOver])

  const handleRefetchQuery = async () => {
    await queryClient.invalidateQueries(['GET_REPORT_DATA'])
  }

  useEffect(() => {
    if (isSuccess || deleteCheckpointGroupPhoto.isSuccess) {
      handleCloseForm()
      handleRefetchQuery()
    } else {
      if (updateCheckpointMutation.isError) {
        notification.error({
          message: error?.response?.data.message,
          duration: 3,
          placement: 'top',
        })
      }
    }
  }, [isSuccess, isError, deleteCheckpointGroupPhoto.isSuccess])

  useEffect(() => {
    if (!props.open) {
      form.resetFields()
    } else {
      const { name, value, control_type, photoSelected, checkpointKey, id }: any =
        props.data
      const wrapFormValueFN = (v) => {
        if (includes([TEXT, NUMBER], control_type)) {
          let result = null

          if (v) {
            result = v
          }

          return result
        }

        if (includes([DATE], control_type)) {
          return v ? moment.utc(v) : null
        }

        if (includes([MULTI_SELECT], control_type)) {
          let result = []

          if (size(v)) {
            result = v
          }

          return result
        }

        if (includes([EDITABLE_ENTRY_LIST], control_type)) {
          let result = v

          if (size(v) < 2) {
            if (isEqual(v, '') || isEqual(v, [''])) {
              result = null
            }
          }

          return result
        }

        if (includes([SELECT, SELECT_DROPDOWN], control_type)) {
          let result = null

          if (v.label || v.value) {
            result = v
          }

          return sanitizeSelectValueForDependency({
            value: result,
            checkpointName: name,
            controlType: control_type,
            templateSections: props.dependencyTemplateSections,
            reportSections: props.reportSections,
          })
        }
        if (includes([GROUP], control_type)) {
          const photoCheckpoint = checkpointKey
            ? find(
                entries(groupCheckpointData),
                ([key]: any) => key === checkpointKey
              )
            : find(
                entries(groupCheckpointData),
                ([_, value]: any) => value.control_type === 'photo'
              )

          if (size(photoCheckpoint) && photoSelected) {
            const [matchKey, matchValue]: any = photoCheckpoint
            const pickImageDelete = find(
              matchValue.value,
              (item) => item.thumbnailId === photoSelected.thumbnailId
            )

            setStateIsDeletePhoto({
              checkpointName: matchKey,
              entryIndex: props.data?.entryIndex,
              subSectionId: id,
              ...pickImageDelete,
            })
          }

          return groupCheckpointData
        }

        return null
      }

      const getValue = wrapFormValueFN(value) || null

      const result = { [`${name}`]: getValue }

      form.setFieldsValue(result)
    }
  }, [props.open, groupCheckpointData])

  const renderItemGroup = (data, dataSource) => {
    const {
      name,
      entryIndex,
      photoSelected = null,
      addNewPhotoKey = '',
      checkpointKey = '',
    } = data
    const { checkpoints } = dataSource
    return (
      <Form.Item name={name}>
        <GroupCheckpointComponent
          entryIndex={entryIndex}
          photoSelected={photoSelected}
          addNewPhotoKey={addNewPhotoKey}
          checkpointKey={checkpointKey}
          checkpoints={checkpoints} // require to find title of checkpoint
        />
      </Form.Item>
    )
  }

  const renderMultipleSelect = (dataSource) => {
    const { data_source } = dataSource || {}

    return (
      <Select
        mode="multiple"
        allowClear
        size="large"
        placeholder="Please select"
        options={data_source}
        labelInValue
      />
    )
  }

  const renderEditChoiceItem = (data, dataSource) => {
    const { name } = data

    const { data_source } = dataSource || {}

    const displayLabel = <p>{startCase(camelCase(name))}</p>

    return (
      <Form.Item name={name} label={displayLabel}>
        {/* FIRST CHILD RECEIVED VALUE FROM THE FORM AUTO */}
        <EditableChoiceListComponent dataSource={data_source} />
      </Form.Item>
    )
  }
  type DATA_SET_TYPE = {
    control_type: ControlType | 'textarea'
    label?: string
    name?: string
    placeholder?: string
  }

  const renderItemSelect = (data, dataSource) => {
    const { name, control_type } = data
    const { data_source } = dataSource || {}
    const displayLabel = <p>{startCase(camelCase(name))}</p>

    const { options, disabled, helperText } = resolveDependentSelectState({
      checkpointName: name,
      controlType: control_type,
      dataSource: data_source,
      templateSections: props.dependencyTemplateSections,
      reportSections: props.reportSections,
    })

    return (
      <Form.Item
        name={name}
        label={displayLabel}
        extra={
          helperText ? (
            <span className="dependency-helper-text">{helperText}</span>
          ) : null
        }
      >
        <Select
          allowClear
          placeholder="Select"
          options={options}
          labelInValue
          disabled={disabled}
        />
      </Form.Item>
    )
  }

  const renderItemMultipleSelect = (data, dataSource) => {
    const { name, label } = data

    const displayLabel = <p>{startCase(camelCase(name))}</p>

    return (
      <Form.Item name={name} label={displayLabel}>
        {renderMultipleSelect(dataSource)}
      </Form.Item>
    )
  }

  const renderItemText = (data) => {
    const { name, placeholder = 'Text' } = data
    const displayLabel = <p>{startCase(camelCase(name))}</p>

    return (
      <Form.Item name={name} label={displayLabel}>
        <Input placeholder={placeholder} size="large" />
      </Form.Item>
    )
  }

  const renderItemNumber = (data, dataSource) => {
    const { name, title } = data
    const displayLabel = <p>{startCase(camelCase(title || name))}</p>
    return (
      <Form.Item name={name} label={displayLabel}>
        <InputNumber
          style={{ width: '100%' }}
          // onChange={() => setFieldChange(true)}
          placeholder="Number"
          size="large"
          onWheel={(e: any) => e.target.blur()}
          formatter={(value) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          }
          controls={false}
        />
      </Form.Item>
    )
  }

  const renderItemDate = (data, dataSource) => {
    const { name } = data
    const displayLabel = <p>{startCase(camelCase(name))}</p>

    return (
      <Form.Item name={name} label={displayLabel}>
        <DatePicker style={{ marginBottom: 8 }} format={DDMMYY} size="large" />
      </Form.Item>
    )
  }

  const renderItemTextarea = (data, dataSource) => {
    const { name, placeholder } = data

    const displayLabel = <p>{startCase(camelCase(name))}</p>
    return (
      <Form.Item name={name} label={displayLabel}>
        <TextArea autoSize placeholder={placeholder} size="large" />
      </Form.Item>
    )
  }

  const renderItemEditChoiceItem = (dataItem, dataSource) => {
    return (
      <div className="editable-choice-container">
        {renderEditChoiceItem(dataItem, dataSource)}
      </div>
    )
  }

  const handleRenderFormItem = (
    // dataItem: DATA_SET_TYPE,
    dataItem: any,
    dataSource: any = []
  ) => {
    let getDataSourceItem: any = {}
    if (dataItem.control_type !== GROUP) {
      const { checkpoints } = dataSource || {}
      getDataSourceItem = filter(
        checkpoints,
        (item) => item.name === dataItem.name
      )[0]
    }

    const { control_type } = dataItem

    if (includes([GROUP], control_type)) {
      return renderItemGroup(dataItem, dataSource)
    }

    if (includes([EDITABLE_ENTRY_LIST], control_type)) {
      return renderItemEditChoiceItem(dataItem, getDataSourceItem)
    }

    if (includes([TEXT], control_type)) {
      return renderItemText(dataItem)
    }

    if (includes([NUMBER], control_type)) {
      return renderItemNumber(dataItem, getDataSourceItem)
    }

    if (includes([SELECT, SELECT_DROPDOWN], control_type)) {
      return renderItemSelect(dataItem, getDataSourceItem)
    }

    if (includes([MULTI_SELECT], control_type)) {
      return renderItemMultipleSelect(dataItem, getDataSourceItem)
    }
    if (includes([DATE], control_type)) {
      return renderItemDate(dataItem, getDataSourceItem)
    }

    if (includes(['textarea'], control_type)) {
      return renderItemTextarea(dataItem, getDataSourceItem)
    }

    return <></>
  }

  const onValuesFormFN = (e) => {
    const { control_type, value } = props.data

    if (!includes([GROUP], control_type)) {
      const getValues = values(e)[0]
      const valueEqual = isEqual(getValues, value)

      setStateEnableSubmitButton(valueEqual)
    } else {
      setStateEnableSubmitButton(false)
    }
  }

  const onFinishedFormFN = async (e) => {
    const { id, control_type, entryIndex, name } = props.data
    const payload = {
      id,
      payload: {
        control_type,
        value: e,
        entryIndex,
        checkpointName: name,
      },
    }

    await updateCheckpointMutation.mutateAsync(payload)

    const sectionName = props.dataSource?.sectionName
    const subSectionName = props.dataSource?.name
    const reference =
      sectionName && subSectionName && name
        ? `${sectionName}.${subSectionName}.${name}`
        : null

    if (
      includes([SELECT, SELECT_DROPDOWN], control_type) &&
      reference &&
      props.dependencyTemplateSections?.length
    ) {
      const toClear = getInvalidDependentsToClear({
        reference,
        newTriggerValue: getTriggerAnswerValue(values(e)[0]),
        templateSections: props.dependencyTemplateSections,
        reportSections: props.reportSections,
      })

      const cleanupErrors: string[] = []
      let clearedCount = 0

      for (const dependent of toClear) {
        try {
          await clearDependentCheckpointMutation.mutateAsync({
            id: dependent.id,
            payload: {
              control_type: dependent.control_type || SELECT_DROPDOWN,
              value: {
                [dependent.name]: { label: '', value: '' },
              },
              entryIndex: null,
              checkpointName: dependent.name,
            },
          })
          clearedCount += 1
        } catch {
          cleanupErrors.push(dependent.name)
        }
      }

      if (clearedCount > 0) {
        await handleRefetchQuery()
      }

      if (cleanupErrors.length) {
        notification.error({
          message: `Saved, but failed to clear ${
            cleanupErrors.length
          } dependent answer(s): ${cleanupErrors.join(', ')}`,
          duration: 5,
          placement: 'top',
        })
      }
    }
  }

  const handleRenderForm = () => {
    // Temp condition
    if (!size(props.data) || !size(props.dataSource)) return

    if (taskGroupEntryLoading) {
      return (
        <p style={{ textAlign: 'center' }}>
          <Spin size="small" />
        </p>
      )
    }

    return (
      <Form
        form={form}
        onFinish={onFinishedFormFN}
        onValuesChange={onValuesFormFN}
        autoComplete="off"
        layout="vertical"
      >
        {handleRenderFormItem(props.data, props.dataSource)}
        <Form.Item label={null} className="button-container">
          {stateIsDeletePhoto && (
            <Button
              disabled={updateCheckpointMutation.isLoading}
              onClick={() => setStateConfirmDelete(true)}
              size="large"
              className="btn-delete"
              icon={<DeleteOutlined />}
            />
          )}
          <Button
            disabled={updateCheckpointMutation.isLoading}
            onClick={() => handleCloseForm()}
            className="cancel"
            size="large"
          >
            Cancel
          </Button>
          <Button
            size="large"
            type="primary"
            htmlType="submit"
            disabled={
              stateEnableSubmitButton || updateCheckpointMutation.isLoading
            }
            loading={updateCheckpointMutation.isLoading}
          >
            Save
          </Button>
        </Form.Item>
      </Form>
    )
  }

  const handleRenderTitle = () => {
    let title = <p>Edit Checkpoint</p>

    if (includes([GROUP], controlType)) {
      title = <p>Edit Entry</p>
    }

    return (
      <Row align={'middle'} className="title-container">
        <EditOutlined />
        {title}
      </Row>
    )
  }

  return (
    <Drawer
      title=""
      placement={'right'}
      closable={false}
      open={props.open}
      key={'right'}
      width={400}
      onMouseOver={() => {
        setMouseOver(true)
      }}
      onMouseLeave={() => setMouseOver(false)}
    >
      <div className="drawer-container">
        <Button
          onClick={() => handleCloseForm()}
          className="button-close"
          icon={<DoubleRightOutlined className="close-icon" />}
        />
        {handleRenderTitle()}
        <p className="section-name">
          {props.dataSource?.sectionPoint}{' '}
          {startCase(camelCase(props.dataSource?.title))}
        </p>
        {handleRenderForm()}
      </div>
      {stateConfirmDelete && (
        <ModalConfirm
          content={
            <div>
              Are you sure you want to delete this photo? You won’t be able to
              undo this action.
            </div>
          }
          onClose={() => setStateConfirmDelete(false)}
          title="Delete Photo"
          open={stateConfirmDelete}
          onOK={() => handleDeletePhoto(stateIsDeletePhoto)}
          onCancel={() => setStateConfirmDelete(false)}
          cancelButtonText="No, Cancel"
          type="delete"
          confirmButtonText="Yes, Delete"
        />
      )}
    </Drawer>
  )
}

export default InspectionReportDrawer
